import React, { useState } from "react";
import { useDispatch } from "react-redux";

// material-ui
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import Typography from "@material-ui/core/Typography";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";

//charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/line";
import "echarts/lib/chart/gauge";
import "echarts/lib/chart/bar";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";

//icons
import StopIcon from "mdi-material-ui/CloseOctagonOutline";

import { MuiTable } from "@jazasoft/mui-table";
import { showNotification } from "jazasoft";

// local
import CardHeader from "../../components/CardHeader";
import ConfirmDialog from "../../components/ConfirmDialog";

// helpers
import { TaskState } from "../../utils/types";
import useScheduledTask from "./useScheduledTask";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles({
  tileContainer: {
    padding: "3em 1.5em",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: 147
  },
  tileRow1: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  alignLeft: {
    textAlign: "left"
  }
});

const ScheduledTaskRealtime = ({ tabIdx }) => {
  const classes = useStyles();
  const { dataSummary, echartOptions, columns, rows, onTaskDelete } = useScheduledTask({ page: "realtime" });

  // state
  const [dialogState, setDialogState] = useState({ active: false, taskId: null, cb: null }); // name: delete, taskId: id of task to be stopped, callback: onActionComplete

  // hooks
  const dispatch = useDispatch();

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (action === "stop") {
      if (row.state === TaskState.NEW) {
        setDialogState({ active: true, taskId: row.id, cb: onActionComplete });
      } else {
        dispatch(showNotification("Only Scheduled Tasks in 'New' State can be cancelled", { type: "danger" }));
      }
    }
  };

  const onConfirmDelete = () => {
    onTaskDelete(dialogState.taskId, dialogState.cb);
    setDialogState({});
  };

  if (tabIdx !== 0) return null;

  return (
    <div>
      <ConfirmDialog
        open={dialogState?.active || false}
        labelConfirm="resources.scheduledTask.button.stop"
        labelCancel="resources.scheduledTask.button.cancel"
        confirmTitle="Stop this task?"
        onConfirm={onConfirmDelete}
        onCancel={_ => setDialogState({})}
      />

      <Grid container spacing={2}>
        {/* Row 1 */}
        <Grid item xs={12} sm={4}>
          {/* Overall tiles - New, Waiting, Running, Completed */}
          <Grid container spacing={2}>
            {dataSummary.map(({ label, value, icon }, idx) => (
              <Grid key={idx} item xs={6}>
                <Card>
                  <div className={classes.tileContainer}>
                    <div className={classes.tileRow1}>
                      <div>
                        <Typography variant="h4" className={classes.alignLeft}>
                          {value}
                        </Typography>
                      </div>
                      <div>{icon}</div>
                    </div>

                    <div>
                      <Typography color="textSecondary" className={classes.alignLeft}>
                        {label}
                      </Typography>
                    </div>
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
        {/* Tenant wise tasks */}
        <Grid item xs={12} sm={8}>
          <Card>
            <CardHeader title="Client Wise Task" />
            <Divider />
            <CardContent style={{ padding: "0 0 8px 0" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={echartOptions.barChartMultiple}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader title="Timeline: Task Status" />
            <Divider />
            <CardContent style={{ padding: "0 0 8px 0" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={echartOptions.stateTimeline}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card>
            <CardHeader title="Waiting + Processing / Processing time (in seconds)" />
            <Divider />
            <CardContent style={{ padding: "0 0 8px 0" }}>
              <ReactEchartsCore
                echarts={echarts}
                option={echartOptions.processingTimeline}
                style={{ height: "250px", width: "100%" }}
                opts={{ renderer: "svg" }}
                className="react_for_echarts"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Table */}
        <Grid item xs={12} sm={12}>
          <MuiTable
            key={JSON.stringify(rows.map(e => e.modifiedAt))}
            columns={columns}
            rows={rows}
            title="Tasks"
            fontSize={15}
            pageable
            searchable
            sortable
            inlineActions={[{ name: "stop", tooltip: "Stop", icon: <StopIcon style={{ color: "red" }} /> }]}
            onInlineActionClick={onInlineActionClick}
            actionPlacement="right"
            defaultSort={{ field: "createdAt", order: "desc" }}
            searchKeys={["poRef"]}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ScheduledTaskRealtime;
